import { useEffect, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';

// Add Tag (Jason, 28 Oct 2026): the retailer's Vend is not ours to tag, so a tag can be made here
// first. It joins the list with source Manual and 0 products, then is typed onto products
// (Products page, Pending Review / Flagged edit, xlsx import).

export interface NewTag {
  tag: string;
  role: 'chip' | 'ignore';
  alsoShowOnCategoryId: string | null;
}

export default function AddTagDialog({
  open, categories, saving, onClose, onSave,
}: {
  open: boolean;
  categories: Array<{ id: string; name: string }>;
  saving: boolean;
  onClose: () => void;
  onSave: (tag: NewTag) => void;
}) {
  const [tag, setTag] = useState('');
  const [role, setRole] = useState<'chip' | 'ignore'>('chip');
  const [categoryId, setCategoryId] = useState('');

  // a fresh form every time it opens
  useEffect(() => {
    if (open) { setTag(''); setRole('chip'); setCategoryId(''); }
  }, [open]);

  const name = tag.trim();
  const problem = name.includes(',') ? 'A tag cannot contain a comma' : name.length > 60 ? 'At most 60 characters' : '';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add tag</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            label="Tag"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            error={!!problem}
            helperText={problem || 'The name customers see on the chip.'}
            inputProps={{ maxLength: 60 }}
          />
          <div>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Role</Typography>
            <ToggleButtonGroup size="small" exclusive value={role}
              onChange={(_, v: 'chip' | 'ignore' | null) => { if (v) { setRole(v); if (v === 'ignore') setCategoryId(''); } }}
              aria-label="New tag role">
              <ToggleButton value="chip" sx={{ textTransform: 'none' }}>Chip</ToggleButton>
              <ToggleButton value="ignore" sx={{ textTransform: 'none' }}>Ignore</ToggleButton>
            </ToggleButtonGroup>
          </div>
          <FormControl size="small" disabled={role !== 'chip'}>
            <InputLabel id="new-tag-category">Also show on category</InputLabel>
            <Select labelId="new-tag-category" label="Also show on category" value={categoryId}
              onChange={(e) => setCategoryId(e.target.value as string)}>
              <MenuItem value="">None</MenuItem>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            It starts with 0 products. Add it to products on the Products page, or in Pending Review and Flagged Items.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={!name || !!problem || saving}
          onClick={() => onSave({ tag: name, role, alsoShowOnCategoryId: role === 'chip' && categoryId ? categoryId : null })}>
          Add tag
        </Button>
      </DialogActions>
    </Dialog>
  );
}
